/**
 * Tier-1 shared types for the personnel domain. The worker authors these
 * (they mirror the Bigin shape); the web consumes them via the api-client.
 *
 * Worker = source of truth for shape. Anything the worker emits but the web
 * doesn't read yet still lives here, so the contract stays singular.
 */

export interface PersonnelPerson {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
}

export interface SeniorMinisterPerson extends PersonnelPerson {
  mailing_street: string | null;
  mailing_city: string | null;
  mailing_state: string | null;
  mailing_zip: string | null;
  mailing_country: string | null;
}

export interface OtherClergryPerson extends PersonnelPerson {
  mailing_street: string | null;
  mailing_city: string | null;
  mailing_state: string | null;
  mailing_zip: string | null;
  mailing_country: string | null;
}

export interface PersonnelData {
  parish: {
    id: string;
    name: string;
    church_type: string | null;
  };
  senior_minister: SeniorMinisterPerson | null;
  other_clergy: OtherClergryPerson[];
  /**
   * Wardens shape varies by church type. For Parish/Provisional Parish:
   *   { vicars_warden, peoples_warden }
   * For Branch/Sub-Branch:
   *   { bishops_warden, peoples_warden }
   * The UI conditionally renders based on `parish.church_type`.
   */
  wardens: {
    vicars_warden: PersonnelPerson | null;
    bishops_warden: PersonnelPerson | null;
    peoples_warden: PersonnelPerson | null;
  };
  parish_council: PersonnelPerson[];
  synod_reps: PersonnelPerson[];
  vpso: PersonnelPerson | null;
  safety_lead_officer: PersonnelPerson | null;
  treasurer: PersonnelPerson | null;
  administrator: PersonnelPerson | null;
  nominators: PersonnelPerson[];
}

/**
 * The shape the web POSTs back when submitting the personnel form. Identical to
 * PersonnelData except the parish is referenced by id alone — kept structurally
 * in sync so the two shapes can never drift apart.
 */
export interface PersonnelSubmission extends Omit<PersonnelData, 'parish'> {
  parish: { id: string };
}

/** An existing Contact at THIS parish that a new person may be. A merge target. */
export interface SameParishMatch {
  id: string;
  full_name: string;
  same_parish: true;
  matched_on: Array<'phone' | 'name'>;
  email: string | null;
  phone: string | null;
  account_name: string | null;
}

/**
 * Someone in ANOTHER parish matched. Carries no Contact id, no name and no
 * initials (cca-0x72d): linking across parishes is refused — Church_Role is
 * shared across the org, so a cross-parish link grants rights in that person's
 * home parish — so the id has no legitimate use here. The page shows a notice
 * ("ask the Registrar to link them"), not a choice, and the new person can
 * still be created.
 */
export interface OtherParishMatch {
  same_parish: false;
  matched_on: Array<'phone' | 'name'>;
}

export type CandidateMatch = SameParishMatch | OtherParishMatch;

export interface PersonChangeSet {
  /** Stable key used for merge directives. */
  key: string;
  id: string;
  email: string;
  name: string;
  isNew: boolean;
  rolesAdded: string[];
  rolesRemoved: string[];
  fieldEdits: Array<{ field: string; from: unknown; to: unknown }>;
  potentialMatches: CandidateMatch[];
}

export interface PersonnelDiff {
  parishId: string;
  seniorMinisterEdits: Array<{ field: string; from: unknown; to: unknown }>;
  otherClergryEdits: Array<{ field: string; from: unknown; to: unknown }>;
  changes: PersonChangeSet[];
  accountLookups: {
    administration_contact?: { id: string } | null;
    administration_contact1?: { id: string } | null;
  };
}

/** The explicit "this is someone new" answer to a same-parish potential match. */
export const CREATE_NEW = 'create_new';

/**
 * change.key → the editor's answer for a new person with potential matches:
 * either {@link CREATE_NEW}, or the id of an existing Contact AT THIS PARISH to
 * link them to. The server refuses (400, nothing written) any other id.
 */
export type MergeDirectives = Record<string, string>;
